import {
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';

/**
 * Fires `closeOnOutsideClick` when the user clicks outside a non-modal
 * `<p-drawer>`.
 *
 * PrimeNG's own outside-click dismissal only works for modal drawers —
 * it keys off the backdrop overlay. We want some drawers to float
 * (so the user can keep dragging kanban cards / scrolling a list behind
 * them) but still dismiss on click-out. Attach this directive to the
 * `<p-drawer>` host; it listens for pointerdown events on document and
 * emits when the target isn't inside the drawer panel or any body-
 * portalled PrimeNG overlay (p-select-overlay, p-datepicker-panel,
 * p-popover, p-menu, p-tooltip).
 *
 * The host wires `(closeOnOutsideClick)` to the signal / handler that
 * flips its visibility. The directive is deliberately not opinionated
 * about open/closed state — it fires on every outside click; the host
 * decides whether to close in response.
 *
 * Opt out per-call by setting `[libDrawerClickOutsideClose]="false"` —
 * e.g. a drawer that must survive outside clicks on a specific form.
 *
 * Usage:
 *   <p-drawer libDrawerClickOutsideClose
 *             (closeOnOutsideClick)="visible.set(false)"
 *             [modal]="false"
 *             ...>
 */
@Directive({
  selector: '[libDrawerClickOutsideClose]',
  standalone: true,
})
export class DrawerClickOutsideCloseDirective implements OnInit {
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /** Toggles the behaviour. Default true. Pass false to opt out without
   *  having to remove the directive. */
  @Input('libDrawerClickOutsideClose') enabled: boolean | '' = true;

  @Output() closeOnOutsideClick = new EventEmitter<void>();

  private readonly onPointerDown = (ev: Event): void => {
    if (this.enabled === false) return;

    const panel = this.host.nativeElement.querySelector('.p-drawer');
    // If the drawer isn't rendered (closed), nothing to dismiss.
    if (!panel) return;

    const target = ev.target as Node | null;
    if (!target || !(target instanceof Element)) return;

    // Click lands inside the drawer itself — leave it open.
    if (panel.contains(target)) return;

    // Click lands inside a body-portalled PrimeNG overlay (select
    // dropdown, datepicker calendar, autocomplete panel, popover,
    // menu, tooltip, dialog). Those are logically part of a drawer-
    // owned interaction even though they sit outside the drawer's
    // DOM subtree. Treat them as "inside" for dismissal purposes.
    if (target.closest(DrawerClickOutsideCloseDirective.OVERLAY_SELECTOR)) return;

    this.closeOnOutsideClick.emit();
  };

  /** CSS selector list of body-portalled PrimeNG overlays that should
   *  NOT trigger a drawer close when clicked. Extend when a new
   *  PrimeNG surface is introduced; keeping the list in one place
   *  means a new overlay type only needs one update. */
  private static readonly OVERLAY_SELECTOR = [
    '.p-overlay',
    '.p-select-overlay',
    '.p-datepicker-panel',
    '.p-datepicker',
    '.p-autocomplete-overlay',
    '.p-multiselect-overlay',
    '.p-dropdown-panel',
    '.p-cascadeselect-overlay',
    '.p-popover',
    '.p-confirmpopup',
    '.p-menu',
    '.p-menu-overlay',
    '.p-tieredmenu',
    '.p-tooltip',
    '.p-dialog',
    '.p-toast',
  ].join(', ');

  ngOnInit(): void {
    // pointerdown rather than click so we fire before Angular runs
    // (click handlers get a chance to call stopPropagation on their
    // own actions, which would prevent the directive from seeing the
    // originating click). capture=true so a child's stopPropagation
    // can't eat the event before we see it.
    document.addEventListener('pointerdown', this.onPointerDown, true);
    this.destroyRef.onDestroy(() =>
      document.removeEventListener('pointerdown', this.onPointerDown, true),
    );
  }
}
